import React from 'react';
import { Link } from 'react-router-dom';

const InvestmentBasics = () => {
  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4">💰 Investment Basics</h1>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Types of Investments</h3>
              <p>There are many ways to invest your money. Here are the most common types:</p>
              <div className="row">
                <div className="col-md-6">
                  <h5>Stocks</h5>
                  <p>Ownership shares in companies that can grow in value over time.</p>
                  <p className="small text-muted">
                    Stocks offer the highest potential returns but also come with higher volatility. 
                    They're ideal for long-term growth and can provide income through dividends.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Bonds</h5>
                  <p>Loans to companies or governments that pay regular interest.</p>
                  <p className="small text-muted">
                    Bonds typically offer lower returns than stocks but with less volatility. 
                    They provide steady income and help balance portfolio risk.
                  </p>
                </div>
              </div>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>ETFs (Exchange-Traded Funds)</h5>
                  <p>Baskets of securities that trade like individual stocks.</p>
                  <p className="small text-muted">
                    ETFs offer instant diversification by tracking indexes, sectors, or asset classes. 
                    They typically have lower fees than mutual funds and trade throughout the day.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Real Estate</h5>
                  <p>Property investments that can generate rental income and appreciate in value.</p>
                  <p className="small text-muted">
                    Real estate can be owned directly or through REITs (Real Estate Investment Trusts). 
                    It offers potential tax advantages and serves as an inflation hedge.
                  </p>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Warren Buffett's investment in Coca-Cola demonstrates the power of long-term stock investing. 
                  In 1988, Buffett's Berkshire Hathaway purchased $1.3 billion worth of Coca-Cola shares. 
                  Today, that investment is worth over $25 billion and generates approximately $640 million 
                  in annual dividend income. This example shows how quality stocks can both appreciate in value 
                  and provide growing income over time.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Building a Portfolio</h3>
              <p>A portfolio is your collection of investments. Diversification is key to reducing risk.</p>
              
              <h5 className="mt-3">Asset Allocation</h5>
              <p>
                Asset allocation is how you divide your investments among different asset classes like stocks, 
                bonds, and cash. Your ideal allocation depends on:
              </p>
              <ul>
                <li><strong>Time Horizon:</strong> How long until you need the money</li>
                <li><strong>Risk Tolerance:</strong> Your comfort level with market fluctuations</li>
                <li><strong>Financial Goals:</strong> What you're investing for (retirement, home purchase, etc.)</li>
              </ul>
              
              <div className="row mt-3">
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-header bg-primary text-white">
                      Conservative Portfolio
                    </div>
                    <div className="card-body">
                      <p><strong>Stocks:</strong> 20-30%</p>
                      <p><strong>Bonds:</strong> 50-60%</p>
                      <p><strong>Cash:</strong> 10-20%</p>
                      <p className="small text-muted">Best for: Short time horizons or low risk tolerance</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-header bg-primary text-white">
                      Moderate Portfolio
                    </div>
                    <div className="card-body">
                      <p><strong>Stocks:</strong> 50-60%</p>
                      <p><strong>Bonds:</strong> 30-40%</p>
                      <p><strong>Cash:</strong> 5-10%</p>
                      <p className="small text-muted">Best for: Medium time horizons or moderate risk tolerance</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-header bg-primary text-white">
                      Aggressive Portfolio
                    </div>
                    <div className="card-body">
                      <p><strong>Stocks:</strong> 70-80%</p>
                      <p><strong>Bonds:</strong> 15-25%</p>
                      <p><strong>Cash:</strong> 0-5%</p>
                      <p className="small text-muted">Best for: Long time horizons or high risk tolerance</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <h5 className="mt-4">Diversification Strategies</h5>
              <p>
                Proper diversification means spreading your investments across:
              </p>
              <ul>
                <li><strong>Asset Classes:</strong> Stocks, bonds, real estate, etc.</li>
                <li><strong>Sectors:</strong> Technology, healthcare, finance, etc.</li>
                <li><strong>Geography:</strong> U.S., international developed markets, emerging markets</li>
                <li><strong>Company Size:</strong> Large-cap, mid-cap, small-cap</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the 2008 financial crisis, a diversified portfolio consisting of 60% stocks and 40% bonds 
                  lost approximately 20% of its value. While significant, this was much better than the S&P 500's 
                  37% decline. Furthermore, this diversified portfolio recovered its losses within 3 years, 
                  demonstrating how proper diversification can reduce losses during market downturns and 
                  position investors for recovery.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Investment Accounts</h3>
              <p>
                Where you hold your investments can be just as important as what you invest in, due to tax implications.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Tax-Advantaged Accounts</h5>
                  <ul>
                    <li>
                      <strong>401(k)/403(b):</strong> Employer-sponsored retirement accounts with tax-deferred growth
                    </li>
                    <li>
                      <strong>Traditional IRA:</strong> Tax-deductible contributions with tax-deferred growth
                    </li>
                    <li>
                      <strong>Roth IRA:</strong> After-tax contributions with tax-free growth and withdrawals
                    </li>
                    <li>
                      <strong>HSA:</strong> Health Savings Accounts offer triple tax advantages for medical expenses
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Taxable Accounts</h5>
                  <ul>
                    <li>
                      <strong>Brokerage Accounts:</strong> Flexible accounts with no contribution limits
                    </li>
                    <li>
                      <strong>Robo-Advisors:</strong> Automated investment platforms with low fees
                    </li>
                    <li>
                      <strong>Direct Stock Purchase Plans:</strong> Buy shares directly from companies
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Consider two investors who each invest $6,000 annually for 30 years, earning an 8% average return. 
                  Investor A uses a Roth IRA, while Investor B uses a taxable account (paying 22% in taxes on gains).
                  After 30 years, Investor A would have approximately $680,000, while Investor B would have about $500,000. 
                  This $180,000 difference demonstrates the significant impact that tax-advantaged accounts can have on 
                  long-term wealth building.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Dollar-Cost Averaging</h3>
              <p>
                Dollar-cost averaging (DCA) is an investment strategy where you invest a fixed amount of money at regular intervals, 
                regardless of market conditions. This approach can reduce the impact of volatility and eliminate the stress of trying to time the market.
              </p>
              
              <h5 className="mt-3">Benefits of Dollar-Cost Averaging</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Reduces Timing Risk:</strong> Avoids the risk of investing a large sum at market peaks
                    </li>
                    <li>
                      <strong>Emotional Discipline:</strong> Creates a systematic approach that removes emotion from investing
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Automatic Bargain Hunting:</strong> Naturally buys more shares when prices are low
                    </li>
                    <li>
                      <strong>Accessibility:</strong> Makes investing manageable for those without large lump sums
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the 2008-2009 financial crisis, an investor who continued to invest $500 monthly in an S&P 500 index fund 
                  would have purchased more shares when the market was down 50%. By 2012, their portfolio would have fully recovered 
                  and been in a profit position, despite investing through one of the worst market crashes in history. This demonstrates 
                  how dollar-cost averaging can turn market downturns into long-term opportunities.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Compound Interest</h3>
              <p>
                Compound interest is often called the "eighth wonder of the world" because it allows your money to grow exponentially 
                over time as you earn returns not just on your initial investment, but also on the accumulated interest.
              </p>
              
              <h5 className="mt-3">The Power of Compounding</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Time Horizon Impact</h6>
                  <p>
                    The longer your investment horizon, the more powerful compounding becomes. Starting early, even with smaller amounts, 
                    can lead to significantly larger results than starting later with larger amounts.
                  </p>
                </div>
                <div className="col-md-6">
                  <h6>The Rule of 72</h6>
                  <p>
                    A quick way to estimate how long it will take to double your money: divide 72 by your expected annual return. 
                    For example, at 8% returns, your money doubles approximately every 9 years (72 ÷ 8 = 9).
                  </p>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  If you invested $10,000 at age 25 and earned an 8% average annual return, by age 65 you would have approximately $217,000 
                  without adding any additional money. In contrast, if you waited until age 35 to make the same $10,000 investment, you would 
                  have only about $100,000 by age 65. This $117,000 difference illustrates the enormous impact of time on compound growth.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Investment Fees</h3>
              <p>
                Fees may seem small as percentages, but they can significantly impact your long-term returns. Understanding and 
                minimizing investment fees is a crucial aspect of successful investing.
              </p>
              
              <h5 className="mt-3">Common Investment Fees</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Expense Ratios:</strong> Annual fees charged by mutual funds and ETFs
                      <p className="small text-muted">
                        Range from 0.03% for low-cost index funds to over 1% for actively managed funds
                      </p>
                    </li>
                    <li>
                      <strong>Trading Commissions:</strong> Fees charged when buying or selling securities
                      <p className="small text-muted">
                        Many brokers now offer commission-free trading for stocks and ETFs
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Advisory Fees:</strong> Fees paid to financial advisors for management services
                      <p className="small text-muted">
                        Typically 0.5% to 1.5% of assets under management annually
                      </p>
                    </li>
                    <li>
                      <strong>Load Fees:</strong> Sales charges on some mutual funds
                      <p className="small text-muted">
                        Can be front-end (paid when buying) or back-end (paid when selling)
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Consider two investors who each invest $100,000 for 30 years with an 8% annual return before fees. Investor A chooses 
                  a low-cost index fund with a 0.1% expense ratio, while Investor B selects an actively managed fund with a 1% expense ratio. 
                  After 30 years, Investor A would have approximately $960,000, while Investor B would have about $760,000. This $200,000 
                  difference (about 21% of the potential final value) demonstrates how even a seemingly small difference in fees can 
                  dramatically impact long-term results.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Sustainable Investing</h3>
              <p>
                Sustainable investing (also known as ESG investing) considers environmental, social, and governance factors 
                alongside financial returns. This approach aims to generate positive returns while also creating positive societal impact.
              </p>
              
              <h5 className="mt-3">ESG Factors</h5>
              <div className="row">
                <div className="col-md-4">
                  <h6>Environmental</h6>
                  <ul>
                    <li>Climate change policies</li>
                    <li>Carbon emissions</li>
                    <li>Resource conservation</li>
                    <li>Waste management</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <h6>Social</h6>
                  <ul>
                    <li>Human rights</li>
                    <li>Labor standards</li>
                    <li>Community relations</li>
                    <li>Data protection</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <h6>Governance</h6>
                  <ul>
                    <li>Board diversity</li>
                    <li>Executive compensation</li>
                    <li>Business ethics</li>
                    <li>Shareholder rights</li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Approaches to Sustainable Investing</h5>
              <ul>
                <li>
                  <strong>Negative Screening:</strong> Excluding companies involved in controversial activities
                </li>
                <li>
                  <strong>Positive Screening:</strong> Selecting companies with positive ESG performance
                </li>
                <li>
                  <strong>Impact Investing:</strong> Targeting investments that generate specific social or environmental benefits
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  The Parnassus Core Equity Fund, one of the oldest ESG-focused funds, has outperformed the S&P 500 over multiple time periods 
                  while maintaining strict environmental and social criteria. From 2010-2020, the fund delivered an average annual return of 
                  approximately 14% compared to the S&P 500's 13.6%. This demonstrates that investors don't necessarily need to sacrifice 
                  returns when investing according to their values.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Rebalancing Your Portfolio</h3>
              <p>
                Rebalancing is the process of realigning the weightings of assets in your portfolio to maintain your desired level of asset allocation.
                Over time, some investments will grow faster than others, causing your portfolio to drift from its original allocation.
              </p>
              
              <h5 className="mt-3">Rebalancing Strategies</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Time-Based Rebalancing</h6>
                  <ul>
                    <li>Rebalance on a regular schedule (quarterly, semi-annually, or annually)</li>
                    <li>Simple to implement and removes emotion from the process</li>
                    <li>May result in unnecessary transactions if portfolio hasn't drifted significantly</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Threshold-Based Rebalancing</h6>
                  <ul>
                    <li>Rebalance when asset allocations drift beyond predetermined thresholds (e.g., ±5%)</li>
                    <li>More responsive to market movements</li>
                    <li>Requires more monitoring but potentially fewer transactions</li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Consider an investor with a 60% stocks/40% bonds portfolio in January 2019. By December 2019, after a strong year for stocks, 
                  the portfolio might have drifted to 68% stocks/32% bonds. By rebalancing back to the original 60/40 allocation, the investor 
                  would have sold high-performing stocks and bought relatively cheaper bonds. When the market crashed in March 2020, this 
                  rebalanced portfolio would have experienced less volatility than the unbalanced one, demonstrating how rebalancing can 
                  help manage risk while systematically buying low and selling high.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Inflation and Investing</h3>
              <p>
                Inflation erodes the purchasing power of money over time. A successful investment strategy must account for inflation 
                to ensure your wealth grows in real terms, not just nominal terms.
              </p>
              
              <h5 className="mt-3">Impact of Inflation on Investments</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Vulnerable to Inflation</h6>
                  <ul>
                    <li>Cash and savings accounts</li>
                    <li>Fixed-rate bonds</li>
                    <li>Fixed annuities</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Inflation Hedges</h6>
                  <ul>
                    <li>Stocks (especially dividend-growing companies)</li>
                    <li>Real estate</li>
                    <li>Treasury Inflation-Protected Securities (TIPS)</li>
                    <li>Commodities</li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Real vs. Nominal Returns</h5>
              <p>
                Real return = Nominal return - Inflation rate
              </p>
              <p>
                A 7% investment return during 3% inflation actually represents a 4% real return. Always consider your returns in real terms.
              </p>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  From 2000 to 2020, the average annual inflation rate in the U.S. was approximately 2.2%. A $10,000 investment in 2000 would 
                  need to grow to about $15,500 by 2020 just to maintain its purchasing power. This illustrates why simply preserving capital 
                  in low-yield savings accounts (which averaged less than 1% during much of this period) actually results in losing wealth in 
                  real terms. In contrast, the S&P 500's average annual return of about 6% during this period provided a real return of 
                  approximately 3.8% after accounting for inflation.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Behavioral Finance</h3>
              <p>
                Behavioral finance examines how psychological influences and biases affect investor behavior and market outcomes.
                Understanding these biases can help you make more rational investment decisions.
              </p>
              
              <h5 className="mt-3">Common Behavioral Biases</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Loss Aversion:</strong> Feeling the pain of losses more acutely than the pleasure of equivalent gains
                      <p className="small text-muted">
                        Can lead to holding losing investments too long and selling winners too early
                      </p>
                    </li>
                    <li>
                      <strong>Recency Bias:</strong> Giving too much weight to recent events when making decisions
                      <p className="small text-muted">
                        Can cause chasing performance or panic selling after market drops
                      </p>
                    </li>
                    <li>
                      <strong>Confirmation Bias:</strong> Seeking information that confirms existing beliefs
                      <p className="small text-muted">
                        Can lead to ignoring warning signs about investments you like
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Herd Mentality:</strong> Following what others are doing rather than making independent decisions
                      <p className="small text-muted">
                        Often leads to buying high and selling low
                      </p>
                    </li>
                    <li>
                      <strong>Overconfidence:</strong> Overestimating your knowledge or ability to predict outcomes
                      <p className="small text-muted">
                        Can result in excessive trading or inadequate diversification
                      </p>
                    </li>
                    <li>
                      <strong>Mental Accounting:</strong> Treating money differently based on its source or intended use
                      <p className="small text-muted">
                        May lead to suboptimal investment decisions across different accounts
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the dot-com bubble of the late 1990s, herd mentality and overconfidence led many investors to pour money into 
                  internet stocks with little or no earnings, driving valuations to unsustainable levels. When the bubble burst in 2000, 
                  the NASDAQ fell by nearly 80% over the next two years. Those who maintained diversified portfolios and avoided getting 
                  caught up in the euphoria were much better positioned to weather the downturn, demonstrating how understanding and 
                  resisting behavioral biases can protect investors from market extremes.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Tax-Efficient Investing</h3>
              <p>
                Tax-efficient investing strategies can significantly increase your after-tax returns over time.
                Understanding how different investments are taxed can help you optimize your portfolio structure.
              </p>
              
              <h5 className="mt-3">Tax Considerations by Account Type</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Tax-Advantaged Accounts</h6>
                  <p>
                    Best for tax-inefficient investments that generate regular income:
                  </p>
                  <ul>
                    <li>Bonds and bond funds</li>
                    <li>REITs</li>
                    <li>Actively managed funds with high turnover</li>
                    <li>Dividend-focused investments</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Taxable Accounts</h6>
                  <p>
                    Best for tax-efficient investments:
                  </p>
                  <ul>
                    <li>Index funds and ETFs with low turnover</li>
                    <li>Growth stocks held for long-term capital gains</li>
                    <li>Municipal bonds (tax-exempt interest)</li>
                    <li>Tax-managed funds</li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Tax-Efficient Strategies</h5>
              <ul>
                <li>
                  <strong>Tax-Loss Harvesting:</strong> Selling investments at a loss to offset capital gains
                </li>
                <li>
                  <strong>Asset Location:</strong> Placing investments in the most tax-appropriate accounts
                </li>
                <li>
                  <strong>Holding Period Management:</strong> Holding investments for at least one year to qualify for lower long-term capital gains rates
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Consider an investor in the 32% tax bracket who needs to allocate $100,000 between a taxable bond fund yielding 4% and an 
                  equity index fund. By placing the bond fund in a tax-advantaged account like an IRA and the equity index fund in a taxable 
                  account, they could save approximately $1,280 in taxes annually compared to the reverse allocation. Over 20 years, this 
                  tax-efficient asset location strategy could result in over $25,000 in additional after-tax wealth, assuming constant rates 
                  and returns.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Alternative Investments</h3>
              <p>
                Alternative investments are assets that fall outside the conventional investment categories of stocks, bonds, and cash.
                They can provide diversification benefits and potentially higher returns, but often come with unique risks and characteristics.
              </p>
              
              <h5 className="mt-3">Types of Alternative Investments</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Private Equity:</strong> Investments in private companies not listed on public exchanges
                      <p className="small text-muted">
                        Higher potential returns but requires long lock-up periods and larger minimum investments
                      </p>
                    </li>
                    <li>
                      <strong>Hedge Funds:</strong> Actively managed funds that use various strategies to generate returns
                      <p className="small text-muted">
                        Can provide returns uncorrelated with traditional markets but often have high fees
                      </p>
                    </li>
                    <li>
                      <strong>Commodities:</strong> Physical goods like gold, oil, agricultural products
                      <p className="small text-muted">
                        Can hedge against inflation but may be volatile and don't produce income
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Cryptocurrency:</strong> Digital or virtual currencies like Bitcoin and Ethereum
                      <p className="small text-muted">
                        Potential for high returns but extremely volatile with regulatory uncertainties
                      </p>
                    </li>
                    <li>
                      <strong>Collectibles:</strong> Art, wine, classic cars, trading cards
                      <p className="small text-muted">
                        Passion investments that may appreciate but lack liquidity and have high transaction costs
                      </p>
                    </li>
                    <li>
                      <strong>Peer-to-Peer Lending:</strong> Direct lending to individuals or small businesses
                      <p className="small text-muted">
                        Provides regular income but carries default risk and limited secondary market
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Considerations for Alternative Investments</h5>
              <ul>
                <li>Typically less liquid than traditional investments</li>
                <li>Often require higher minimum investments</li>
                <li>May have complex fee structures</li>
                <li>Usually best as a small portion of a well-diversified portfolio</li>
                <li>May require specialized knowledge or professional guidance</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the 2008 financial crisis, many traditional investments fell simultaneously, with the S&P 500 dropping 37% and many 
                  bond funds also declining. However, gold rose approximately 5% that year and continued to rise in 2009 and 2010 as investors 
                  sought safe havens. This demonstrates how certain alternative investments can provide valuable diversification during periods 
                  when traditional asset classes are highly correlated. However, gold subsequently underperformed stocks for several years, 
                  highlighting why alternatives are typically best used as a complement to, rather than replacement for, core investments.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3>Key Takeaways</h3>
              <div className="alert alert-info">
                <ul className="mb-0">
                  <li>Different investment types serve different purposes in your portfolio</li>
                  <li>Proper asset allocation should align with your time horizon and risk tolerance</li>
                  <li>Diversification across multiple dimensions helps reduce overall portfolio risk</li>
                  <li>Tax-advantaged accounts can significantly boost your long-term returns</li>
                  <li>Dollar-cost averaging reduces timing risk and enforces investment discipline</li>
                  <li>Compound interest dramatically increases wealth over long time periods</li>
                  <li>Investment fees can significantly impact long-term returns</li>
                  <li>Sustainable investing allows alignment of investments with personal values</li>
                  <li>Regular portfolio rebalancing maintains your risk profile and can enhance returns</li>
                  <li>Investing strategies should account for inflation to preserve purchasing power</li>
                  <li>Understanding behavioral biases can help you make more rational investment decisions</li>
                  <li>Tax-efficient investing strategies can significantly increase after-tax returns</li>
                  <li>Alternative investments can provide diversification beyond traditional assets</li>
                  <li>Start investing early and consistently to harness the power of compounding</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/trading-principles" className="btn btn-primary-custom">
              Next: Trading Principles →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentBasics;
